import React, { useState, useMemo } from 'react';
import { Dumbbell } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { oneRepMaxEpley, oneRepMaxBrzycki, oneRepMaxPercentages } from '../../lib/calculatorUtils';

const OneRepMaxCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [weightKg, setWeightKg] = useState('');
  const [reps, setReps] = useState('');

  const result = useMemo(() => {
    const w = parseFloat(weightKg);
    const r = parseInt(reps, 10);
    if (!w || !r || w < 5 || r < 1 || r > 20) return null;
    const epley = oneRepMaxEpley(w, r);
    const brzycki = oneRepMaxBrzycki(w, r);
    const avg = brzycki ? +((epley + brzycki) / 2).toFixed(1) : epley;
    return { epley, brzycki, avg, percentages: oneRepMaxPercentages(avg) };
  }, [weightKg, reps]);

  const inputs = (
    <div className="space-y-5" data-testid="rm-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Lift Performance</h2>
      <p className="text-slate-400 text-sm mb-5">Enter your last lift's weight and clean reps.</p>
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} label="Weight Lifted" />
      <NumberField id="rm-reps" label="Reps Completed" value={reps} onChange={setReps} placeholder="e.g. 5" suffix="reps" min={1} max={20} testId="rm-reps-input" required />
      <p className="text-slate-500 text-xs">Best accuracy: 3–10 reps to failure.</p>
    </div>
  );

  const display = (val) => unit === 'metric' ? val : +(val * 2.20462).toFixed(1);
  const unitLabel = unit === 'metric' ? 'kg' : 'lbs';

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Estimated 1RM" value={display(result.avg)} unit={unitLabel} accent="from-amber-500 to-orange-500" testId="rm-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Epley formula" value={display(result.epley)} unit={unitLabel} testId="rm-epley" />
        {result.brzycki && <StatRow label="Brzycki formula" value={display(result.brzycki)} unit={unitLabel} testId="rm-brzycki" />}
      </div>
      <div className="mt-6 bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
        <p className="text-slate-300 text-sm font-semibold mb-3">Training percentages</p>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          {result.percentages.map((p) => (
            <div key={p.pct} className="bg-slate-900/40 border border-slate-700/40 rounded-lg p-2" data-testid={`rm-pct-${p.pct}`}>
              <p className="text-slate-500">{p.pct}% · {p.reps}r</p>
              <p className="text-white font-bold">{display(p.weight)}<span className="text-slate-500 ml-0.5">{unitLabel}</span></p>
            </div>
          ))}
        </div>
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        1RM estimates assume good form. Always warm up, use a spotter and progress with caution.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter the weight you lifted and number of reps to estimate your 1RM." />
  );

  return (
    <CalculatorLayout
      slug="one-rep-max" title="One Rep Max Calculator"
      subtitle="Estimate your maximum lifting strength using Epley and Brzycki formulas, with training percentages."
      metaDescription="1RM Calculator to estimate one rep max using Epley & Brzycki formulas, plus training percentages."
      icon={Dumbbell}
      accentGradient="from-amber-500 to-orange-500" accentColor="text-amber-400"
      badgeBg="bg-amber-500/10" badgeBorder="border-amber-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<>
        <p><strong className="text-slate-300">Epley:</strong> 1RM = Weight × (1 + Reps / 30)</p>
        <p><strong className="text-slate-300">Brzycki:</strong> 1RM = Weight × 36 / (37 − Reps)</p>
      </>}
    />
  );
};

export default OneRepMaxCalculatorPage;
